import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Alert, message } from 'antd';
import {
  Modal,
  Text,
  breakpoints as breakpointMap,
  theme,
  ContactMethodsSelector,
} from 'components';
import { AuthContext, LayoutContext } from 'contexts';
import ListingSummary from 'modals/AddListingToTourModal/ListingSummary';
import { useBreakpoint } from 'hooks';
import {
  StatusUpdateType,
  TourItemContent,
  SendAppointmentResponse,
} from '../../types';
import TourEditContext from '../../TourEditContext';
import { useHideChat, useWindowHeight } from '../../hooks';
import showTimeNoLongerAvailableModal from '../../TimeNoLongerAvailableModal';

interface ShowingRequestModalProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const MODAL_HEADER_HEIGHT = 55;
const MODAL_FOOTER_HEIGHT = 53;
const MODAL_SCROLL_THRESHOLD = 950;

const ShowingRequestModal: React.FC<ShowingRequestModalProps> = ({
  visible,
  setVisible,
}) => {
  const { auth } = useContext(AuthContext);
  const {
    topElementHeights: { bbAssume },
  } = useContext(LayoutContext);
  const { sendAppointmentRequests, tourItems } = useContext(TourEditContext);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalOkText, setModalOkText] = useState('Send Requests');
  const [sendStatusUpdatesTo, setSendStatusUpdatesTo] =
    useState<StatusUpdateType>('email');

  const { md } = useBreakpoint();
  const windowHeight = useWindowHeight();

  useHideChat(visible && !md);

  const selectedUser = {
    email: auth?.email,
    phone: auth?.phone,
    officePhone:
      auth?.org.addresses[Number(auth?.location)][Number(auth?.address)].phone,
    bookShowingDefault: auth?.notifications.officeMessages.bookShowingDefault,
  };

  const selectNotifyMethod = (value: StatusUpdateType) =>
    setSendStatusUpdatesTo(value);

  const handleOk = async () => {
    setConfirmLoading(true);
    setModalOkText('Sending');

    const results: SendAppointmentResponse = await sendAppointmentRequests(
      sendStatusUpdatesTo,
    );

    const itemsWithUnavailableTime: TourItemContent[] = [];

    results.forEach(res => {
      if ('tourItemId' in res) {
        if (res.timeNotAvailable) {
          const erroredTourItem: TourItemContent = tourItems.find(
            tourItem => tourItem.tourItemId === res.tourItemId,
          );
          if (erroredTourItem) itemsWithUnavailableTime.push(erroredTourItem);
        }
      }
    });

    if (itemsWithUnavailableTime.length) {
      showTimeNoLongerAvailableModal({
        itemsWithUnavailableTime,
        itemsWithoutCoords: [],
      });
    }

    setVisible(false);
    message.success('Requests sent');
    setSendStatusUpdatesTo('email');
    setModalOkText('Send Requests');
    setConfirmLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const modalHeight = useMemo(() => {
    const size =
      windowHeight > MODAL_SCROLL_THRESHOLD
        ? breakpointMap.md
        : breakpointMap.sm;

    return size - MODAL_HEADER_HEIGHT - MODAL_FOOTER_HEIGHT;
  }, [windowHeight]);

  return (
    <StyledModal
      title="Send Requests"
      open={visible}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      onCancel={handleCancel}
      okText={modalOkText}
      cancelButtonProps={{
        style: { fontWeight: 400, ...(!md && { display: 'none' }) },
      }}
      okButtonProps={{
        style: { fontWeight: 400 },
        block: !md,
        disabled: !sendStatusUpdatesTo,
      }}
      bodyStyle={{
        overflow: 'overlay',
        ...(!md
          ? {
              height: `calc(${windowHeight}px - ${
                MODAL_HEADER_HEIGHT + MODAL_FOOTER_HEIGHT
              }px - ${bbAssume}px)`,
            }
          : {
              maxHeight: `${modalHeight}px`,
            }),
      }}
      width={md ? '660px' : '100vw'}
      $md={md}
      transitionName={!md ? 'ant-move-down' : undefined}
      style={{ ...(!md && { top: bbAssume }) }}
    >
      <StyledModalContentContainer>
        <Alert
          message="You are about to send the following showing requests for approval."
          type="info"
          showIcon
        />
        <StyledText>Send status updates to</StyledText>
        <ContactMethodsSelector
          selectedUser={selectedUser}
          selectNotifyMethod={selectNotifyMethod}
          hasSmsFeature={auth?.org.features.includes('SHOWING_AGENT_SMS')}
        />

        {tourItems
          .filter(tourItem => tourItem.type === 'APPOINTMENT' && tourItem.time)
          .map(
            ({
              id,
              org,
              time,
              timeEnd,
              originalTime,
              originalTimeEnd,
              timezone,
              address,
              locationName,
              mls_numbers,
              thumbnail,
              listingStatus,
              availability,
              status,
            }) => (
              <ListingSummary
                isShowingRequestItem
                id={id}
                org={md && org}
                time={time}
                timeEnd={timeEnd}
                originalTime={originalTime}
                originalTimeEnd={originalTimeEnd}
                timezone={timezone}
                address={address}
                locationName={locationName}
                mls_numbers={mls_numbers}
                thumbnail={thumbnail}
                status={listingStatus}
                availability={availability}
                key={id}
                appointmentStatus={status}
              />
            ),
          )}
      </StyledModalContentContainer>
    </StyledModal>
  );
};

export default ShowingRequestModal;

const StyledModal = styled(Modal)<{ $md?: boolean }>`
  ${({ $md }) =>
    !$md &&
    `
      position: absolute;
      left: 0px;
      top: 0px;
      max-width: unset;
      margin: unset;
      padding: unset;
  `}

  .ant-btn-loading,  .ant-btn-loading:hover, .ant-btn-loading:focus {
    color: ${theme.white};
    background: ${theme.primaryColor};
    svg {
      color: ${theme.white};
    }
  }

  .ant-modal-title {
    text-align: ${({ $md }) => ($md ? 'left' : 'center')};
  }
`;

const StyledModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledText = styled(Text)`
  margin-top: 2.4rem;
  margin-bottom: 0.9rem;
  font-weight: 500;
`;
